import { generatePath, NavigateFunction, useNavigate as useReactDomNav } from 'react-router-dom';
import { POSITION_DETAILS_TABS, ROUTES } from '../consts';
import { ROUTE_VIEW_MODE } from '../consts/enums';
import { PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY } from '../pages/AccountEditPanel';

type useNavigateRes = {
  goToPositions: (replace?: boolean) => void;
  goToAccounts: (preservedAccountsFiltersBase64?: string | null) => void;
  goToTalents: () => void;
  goToPositionDetails: (id: string, tab?: string) => void;
  goToTalentsEdit: (id: string) => void;
  goToTalentHistoryLog: (id: string) => void;
  goToTalentView: (id: string) => void;
  goToTalentsNew: () => void;
  goToAccountEdit: (
    id: string,
    queryParams: string,
    preservedAccountsFiltersBase64?: string | null
  ) => void;
  goToAccountView: (
    id: string,
    queryParams: string,
    preservedAccountsFiltersBase64?: string | null
  ) => void;
  goToMatchEditWithModalProp: (
    positionId: string,
    accountId: string,
    matchId: string,
    isOpen: boolean
  ) => void;
  goToPositionWithModalProp: (id: string) => void;
  goBack: () => void;
  goToMatchEdit: (
    positionId: string,
    accountId: string,
    matchId: string,
    replace?: boolean
  ) => void;
  goToMatchView: (
    positionId: string,
    accountId: string,
    matchId: string,
    replace?: boolean
  ) => void;
  goToPositionsFilteredByAccount: (accountId: string) => void;
  goToCreateAccount: (userEmail?: string) => void;
  goToChoosePlan: (
    accountId: string,
    userEmail: string,
    isNewAccount: boolean
  ) => void;
  navigate: NavigateFunction;
};

export type LocationState = {
  isOpen: boolean;
};

export default function useNavigate(): useNavigateRes {
  const navigate = useReactDomNav();

  return {
    goToPositions: (replace = false) => {
      navigate(ROUTES.POSITIONS, { replace });
    },
    goToPositionDetails: (id: string, tab = POSITION_DETAILS_TABS.APP) => {
      const path = generatePath(ROUTES.POSITION_MATCHES, {
        positionId: id,
      });
      navigate(`${path}?tab=${tab}`);
    },
    goToTalents: () => {
      navigate(ROUTES.TALENTS);
    },
    goToTalentsEdit: (id: string) => {
      const path = generatePath(ROUTES.TALENT_VIEW, {
        talentId: id,
        viewMode: ROUTE_VIEW_MODE.EDIT,
      });
      navigate(path);
    },
    goToTalentView: (id: string) => {
      const path = generatePath(ROUTES.TALENT_VIEW, {
        talentId: id,
        viewMode: ROUTE_VIEW_MODE.VIEW,
      });
      navigate(path);
    },
    goToTalentsNew: () => {
      navigate(ROUTES.TALENTS_NEW);
    },
    goBack: () => {
      navigate(-1);
    },
    goToMatchEdit: (
      positionId: string,
      accountId: string,
      matchId: string,
      replace = false,
    ) => {
      const path = generatePath(ROUTES.MATCH_VIEW, {
        positionId,
        accountId,
        matchId,
        viewMode: ROUTE_VIEW_MODE.EDIT,
      });
      navigate(path, { replace });
    },
    goToMatchView: (
      positionId: string,
      accountId: string,
      matchId: string,
      replace = false,
    ) => {
      const path = generatePath(ROUTES.MATCH_VIEW, {
        positionId,
        accountId,
        matchId,
        viewMode: ROUTE_VIEW_MODE.VIEW,
      });
      navigate(path, { replace });
    },
    goToAccounts: (preservedAccountsFiltersBase64?: string | null) => {
      let decodedQueryParams = '';
      if (preservedAccountsFiltersBase64) {
        try {
          decodedQueryParams = atob(preservedAccountsFiltersBase64);
          // eslint-disable-next-line no-empty
        } catch (e) {}
      }
      navigate(`${ROUTES.ACCOUNTS}?${decodedQueryParams}`);
    },
    goToAccountView: (
      id: string,
      queryParams = '',
      preservedAccountsFiltersBase64?: string | null,
    ) => {
      const path = `${generatePath(ROUTES.ACCOUNT_VIEW, {
        accountId: id,
        viewMode: ROUTE_VIEW_MODE.VIEW,
      })}?${PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY}=${preservedAccountsFiltersBase64}&${queryParams}`;
      navigate(path);
    },
    goToAccountEdit: (
      id: string,
      queryParams = '',
      preservedAccountsFiltersBase64?: string | null,
    ) => {
      const path = `${generatePath(ROUTES.ACCOUNT_VIEW, {
        accountId: id,
        viewMode: ROUTE_VIEW_MODE.EDIT,
      })}?${PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY}=${preservedAccountsFiltersBase64}&${queryParams}`;
      navigate(path);
    },
    goToPositionsFilteredByAccount: (accountId: string) => {
      const path = `${ROUTES.POSITIONS}?accounts=${accountId}`;
      navigate(path);
    },
    goToMatchEditWithModalProp: (
      positionId: string,
      accountId: string,
      matchId: string,
      isOpen: boolean,
    ) => {
      const path = `${generatePath(ROUTES.MATCH_VIEW, {
        positionId,
        accountId,
        matchId,
        viewMode: ROUTE_VIEW_MODE.VIEW,
      })}?showHistory=${isOpen}`;
      navigate(path, {
        replace: false,
        state: {
          isOpen,
        },
      });
    },
    goToPositionWithModalProp: (id: string) => {
      navigate(`${ROUTES.POSITIONS}/${id}?showHistory=true`);
    },
    goToTalentHistoryLog: (id: string) => {
      const path = generatePath(ROUTES.TALENT_VIEW, {
        talentId: id,
        viewMode: ROUTE_VIEW_MODE.VIEW,
      });
      navigate(`${path}?showHistory=true`);
    },
    goToCreateAccount: (userEmail: string) => {
      navigate(`${ROUTES.SALES.CREATE_ACCOUNT}?userEmail=${encodeURIComponent(userEmail?.trim()
        ?.toLowerCase())}`);
    },
    goToChoosePlan: (
      accountId: string,
      userEmail: string,
      isNewAccount: boolean,
    ) => {
      navigate(
        `${
          ROUTES.SALES.CHOOSE_PLAN
        }?accountId=${accountId}&userEmail=${encodeURIComponent(userEmail?.trim()
          ?.toLowerCase())
        }&isNewAccount=${isNewAccount}`,
      );
    },
    navigate,
  };
}
